<template>
	<div
		:class="[
			'table helio-table',
			`table--${size}`,
			{ 'table--fixed-height': fixedHeight }
		]"
	>
		<refinement
			v-if="displayableFilters.length > 0 || isSearchable"
			:items="items"
			:filters="displayableFilters"
			:loading="loading"
			:search="search"
			@filter="setFilters"
			@search="criterias.search.term = $event"
		/>

		<template v-if="tableRender">
			<div class="table__header">
				<template v-for="(column, columnIndex) in columns">
					<div
						v-if="isColumnDisplayable(column)"
						:key="`column-${columnIndex}`"
						:class="[
							column.customClass,
							sortClass(column),
							{ 'table__header-block--sortable': column.orderable }
						]"
						:style="{ width: `${column.width}%` }"
						class="table__header-block"
						@click="setSort(column)"
					>
						<div class="table__header-name">
							<div class="table__header-name table__header-text">
								{{ column.name }}
							</div>

							<helio-icon
								v-if="column.orderable"
								:token="sortIcon(column)"
								weight="bold"
								class="table__header-icon"
							/>
						</div>
					</div>
				</template>
			</div>

			<div
				v-if="!loading"
				ref="row-wrapper"
				:class="[
					'table__row-wrapper',
					`table__row-wrapper--${_uid}`,
				]"
				:style="maxHeightStyle"
				@scroll="handleScroll"
			>
				<helio-link
					v-for="(item, itemIndex) in itemsBuffer"
					:key="`row-${_uid}-${itemIndex}`"
					:link="(linkItemKey && item[linkItemKey]) ? item[linkItemKey] : null"
				>
					<div
						:ref="`row-${_uid}-${itemIndex}`"
						:class="[
							'table__row',
							item.customClass,
							{
								'table__row--active': activeIndex === itemIndex,
							},
						]"
						@mouseover="onMouseover(itemIndex)"
						@mouseleave="onMouseleave"
						@click="$emit('row-clicked')"
					>
						<template v-for="(column, columnIndex) in columns">
							<div
								v-if="isColumnDisplayable(column)"
								:key="`row-block-${columnIndex}-${itemIndex}`"
								:style="{ width: `${column.width}%` }"
								:class="[
									'table__row-block',
									column.customClass,
									{
										'table__row-block--raw': isColumnRaw(column, item),
									},
								]"
							>
								<template v-if="$scopedSlots[column.identifier]">
									<slot
										:name="column.identifier"
										:item="item"
									/>
								</template>

								<template v-else-if="column.property && item[column.property]">
									{{ item[column.property] }}
								</template>
							</div>
						</template>
					</div>
				</helio-link>

				<template>
					<slot name="footer" />
				</template>
			</div>

			<div
				v-else
				class="table__row-wrapper"
				:style="maxHeightStyle"
			>
				<div
					v-for="(n, skeletonIndex) in skeletonCount"
					:key="`row-${skeletonIndex}`"
					class="table__row skeleton"
				>
					<template v-for="(column, columnIndex) in columns">
						<div
							v-if="isColumnDisplayable(column)"
							:key="`row-block-${columnIndex}-${skeletonIndex}`"
							:style="{ width: `${column.width}%` }"
							:class="[
								'table__row-block',
								column.customClass,
							]"
						>
							<div
								v-if="$scopedSlots[column.identifier]"
								:class="['table__row-item', `table__row-item--${column.identifier}`]"
							/>

							<div
								v-else-if="column.property"
								class="table__row-item"
							/>
						</div>
					</template>
				</div>

				<template>
					<slot name="footer" />
				</template>
			</div>

			<empty-table
				v-if="itemsBuffer.length === 0 && !loading"
				:criterias="criterias"
			/>
		</template>

		<template v-if="mediumRender">
			<div
				v-if="!loading"
				:class="[
					'table__row-wrapper',
					`table__row-wrapper--${_uid}`,
				]"
				:style="maxHeightStyle"
			>
				<div
					v-for="(item, index) in itemsBuffer"
					:key="index"
					class="table__item"
				>
					<slot
						name="medium"
						:item="item"
					/>
				</div>
			</div>

			<div
				v-else
				class="table__row-wrapper"
				:style="maxHeightStyle"
			>
				<div
					v-for="(n, skeletonIndex) in skeletonCount"
					:key="skeletonIndex"
					class="table__item skeleton"
				>
					<slot
						name="medium"
						:item="{}"
					/>
				</div>
			</div>

			<empty-table
				v-if="itemsBuffer.length === 0 && !loading"
				:criterias="criterias"
			/>
		</template>

		<template v-if="smallRender">
			<div
				v-if="!loading"
				:class="[
					'table__row-wrapper',
					`table__row-wrapper--${_uid}`,
				]"
				:style="maxHeightStyle"
			>
				<div
					v-for="(item, index) in itemsBuffer"
					:key="index"
					class="table__item"
				>
					<slot
						name="small"
						:item="item"
					/>
				</div>
			</div>

			<div
				v-else
				class="table__row-wrapper"
				:style="maxHeightStyle"
			>
				<div
					v-for="(n, skeletonIndex) in skeletonCount"
					:key="skeletonIndex"
					class="table__item skeleton"
				>
					<slot
						name="small"
						:item="{}"
					/>
				</div>
			</div>

			<empty-table
				v-if="itemsBuffer.length === 0 && !loading"
				:criterias="criterias"
			/>
		</template>
	</div>
</template>

<script>
import Fuse from 'fuse.js';
import Icon from '../icon/Icon.vue';
import EmptyTable from './empty-table/EmptyTable.vue';
import Refinement from './refinement/Refinement.vue';
import Link from '../link/Link.vue';

export default {
	name: 'Table',

	components: {
		'helio-icon': Icon,
		'helio-link': Link,
		Refinement,
		EmptyTable,
	},

	props: {
		activeItemKey: {
			type: [Number, String],
			required: false,
			default: '',
			description: 'Sets the active item key to which to scroll to (from the outside of the component)',
		},

		columns: {
			type: Array,
			required: true,
			description: 'Supplies the parameters for each column in the Table',
		},

		filters: {
			type: Array,
			required: false,
			default: null,
			description: 'Supplies the parameters for each of the Table\'s filter options',
		},

		fixedHeight: {
			type: Number,
			required: false,
			default: null,
		},

		itemKey: {
			type: String,
			required: false,
			default: 'id',
			description: 'Sets the primary key of all items',
		},

		items: {
			type: Array,
			required: true,
			description: 'Supplies the content for each of the Table\'s entries',
		},

		linkItemKey: {
			type: String,
			required: false,
			default: null,
			description: 'Sets the key to use as link destination of all items on row click',
		},

		loading: {
			type: Boolean,
			required: false,
			default: false,
			description: 'Sets whether the Table will be shown in its loading state',
		},

		rootWidthLarge: {
			type: Number,
			required: false,
			default: 1024,
			validator: (width) => width > 0,
			description: 'Supplies root width for large screens',
		},

		rootWidthMedium: {
			type: Number,
			required: false,
			default: 420,
			validator: (width) => width > 0,
			description: 'Supplies root width for medium screens',
		},

		search: {
			type: Object,
			required: false,
			default: null,
			validator: (search) => search && search.keys && search.placeholder,
			description: 'Supplies the parameters for the Table\'s search option',
		},

		skeletonCount: {
			type: Number,
			required: false,
			default: 5,
			validator: skeletonCount => skeletonCount >= 0,
			description: 'Sets the number of skeleton Table items to show users during the loading state',
		},
	},

	slots: {
		columnIdentifier: {
			required: false,
			scoped: true,
			description: 'Permits the creation of a slot for each column, which will supply contents to be displayed in a "box" in that column.',
		},

		footer: {
			required: false,
			scoped: true,
			description: 'Supplies content that will appear in the scroll container below the table at the large width',
		},

		medium: {
			required: false,
			scoped: true,
			description: 'Supplies content to Table when its container is at the medium width',
		},

		small: {
			required: false,
			scoped: true,
			description: 'Supplies content to Table when its container is at the small width',
		},
	},

	data() {
		return {
			rootWidth: 0,
			itemsBuffer: [],
			displayableFilters: [],
			criterias: {
				sort: {
					column: null,
					direction: 'none',
				},
				search: {
					term: null,
					options: {
						shouldSort: true,
						threshold: 0.15,
						location: 0,
						distance: 9999,
						maxPatternLength: 32,
						minMatchCharLength: 1,
						keys: [],
					},
				},
				filters: null,
			},
			currentItem: null,
			currentActiveItemKey: null,
		};
	},

	computed: {
		activeIndex() {
			if (this.currentActiveItemKey) {
				return this.itemsBuffer.findIndex(item => {
					if (item[this.itemKey]) {
						return item[this.itemKey]
							.toString() === this.currentActiveItemKey.toString();
					}

					return false;
				});
			}
			return null;
		},

		tableRender() {
			return ((!this.$scopedSlots.medium && this.size === 'medium')
				|| (!this.$scopedSlots.small && this.size === 'small')
				|| this.size === 'large');
		},

		mediumRender() {
			return this.$scopedSlots.medium && this.size === 'medium';
		},

		smallRender() {
			return this.$scopedSlots.small && this.size === 'small';
		},

		size() {
			if (this.rootWidth > this.rootWidthLarge) {
				return 'large';
			}

			if (this.rootWidth > this.rootWidthMedium) {
				return 'medium';
			}

			return 'small';
		},

		isSearchable() {
			return this.search && (!this.search.display || this.search.display[this.size] === true);
		},

		maxHeightStyle() {
			if (this.fixedHeight) {
				return {
					'max-height': `${this.fixedHeight}px`,
				};
			}

			return {};
		},
	},

	watch: {
		activeIndex(newValue) {
			// eslint-disable-next-line no-underscore-dangle
			const id = this._uid;
			const containerClass = `.table__row-wrapper--${id}`;

			if (newValue !== null
				&& this.$refs
				&& this.$refs[`row-${id}-${newValue}`]
				&& document.getElementsByClassName(containerClass).length) {
				this.$scrollTo(this.$refs[`row-${id}-${newValue}`][0], 200, {
					container: containerClass,
				});
			}
		},

		activeItemKey(newValue) {
			this.currentActiveItemKey = newValue;
		},

		columns: {
			// eslint-disable-next-line
			handler: function () {
				// When our colums prop change, we want to process the items once again
				this.onResize();
			},
			deep: true,
		},

		criterias: {
			// eslint-disable-next-line
			handler: function () {
				// When our criterias object change, we want to process the items once again
				this.processCriterias();
			},
			deep: true,
		},

		filters: {
			// eslint-disable-next-line
			handler: function () {
				this.onResize();
			},
			deep: true,
		},

		items: {
			// eslint-disable-next-line
			handler: function () {
				// When our items prop changes, we want to process the active items once again
				this.processCriterias();
			},
			deep: true,
		},

		size(value) {
			this.$emit('size-changed', value);
		},
	},

	beforeDestroy() {
		window.removeEventListener('resize', this.onResize);
	},

	mounted() {
		window.addEventListener('resize', this.onResize);
		this.$nextTick(() => {
			this.onResize();
		});
		this.itemsBuffer = this.cloneObject(this.items);

		if (this.search && this.search.keys) {
			this.criterias.search.options.keys = this.search.keys;
		}

		setTimeout(() => {
			this.currentActiveItemKey = this.activeItemKey;
		}, 500);
	},

	methods: {
		handleScroll() {
			if (this.$refs['row-wrapper']) {
				const scrollContainer = this.$refs['row-wrapper'];
				const currentScroll = scrollContainer.scrollTop;
				const threshold = (scrollContainer.scrollHeight - scrollContainer.clientHeight) - 300;

				if (currentScroll >= threshold) {
					this.$emit('touched-bottom');
				}
			}
		},

		onResize() {
			this.rootWidth = this.$el.offsetWidth;
			this.setDisplayableFilters();

			this.$el.dispatchEvent(new Event('load'));
		},

		setDisplayableFilters() {
			const validFilters = [];

			if (!this.filters) {
				this.displayableFilters = [];
				return;
			}

			this.filters.forEach((filter) => {
				if (!filter.display || filter.display[this.size]) {
					validFilters.push(filter);
				}
			});

			this.displayableFilters = validFilters;
		},

		sortIcon(column) {
			if (
				this.criterias.sort.column
				&& this.criterias.sort.column.identifier === column.identifier
				&& this.criterias.sort.direction === 'asc'
			) {
				return column.orderIcon && column.orderIcon.asc ? column.orderIcon.asc : 'caret-up';
			}

			return column.orderIcon && column.orderIcon.desc ? column.orderIcon.desc : 'caret-down';
		},

		sortClass(column) {
			// If the sort identifier is the same as our current column while
			// the sort identifier isn't null, our column is being sorted
			if (
				this.criterias.sort.column
				&& this.criterias.sort.column.identifier === column.identifier
				&& this.criterias.sort.column.identifier !== null
			) {
				return 'table__header-block--sorted';
			}

			return 'table__header-block--unsorted';
		},

		setFilters(filters) {
			// If the filter on which the user clicked is the one currently
			// applied, we removed it. Otherwise, we use it as our filter
			if (
				this.criterias.filters
				&& this.criterias.filters.identifier === filters.identifier
			) {
				this.criterias.filters = null;
			} else {
				this.criterias.filters = filters;
			}
		},

		setSort(column) {
			if (column.orderable) {
				// Jump through the current sorting directions and
				// determine which one should be the next
				switch (this.criterias.sort.direction) {
					case 'asc':
						// Descendant
						this.criterias.sort = {
							direction: 'desc',
							column,
						};
						break;

					case 'none':
						// Ascendant
						this.criterias.sort = {
							direction: 'asc',
							column,
						};
						break;

					case 'desc':
					default:
						// No sort
						this.criterias.sort = {
							direction: 'none',
							column: null,
						};
						break;
				}
			}

			this.$emit('set-sort', {
				identifier: column.identifier,
				direction: this.criterias.sort.direction,
			});
		},

		sortItems(items) {
			const { column, direction } = this.criterias.sort;

			// If we don't have a sort column, there's no sort to be made
			if (!column) {
				return items;
			}

			// If the direction is descendant, we reverse the result of the sort
			const base = direction === 'asc' ? -1 : 1;

			// We either use the sort method provide in the column props
			// or we use our basic alphanumeric sorting method
			const method = column.sort ? column.sort : (a, b) => (
				a[column.identifier] > b[column.identifier]
			);

			// Wrap the conditional around the provided method
			const methodWrapper = (a, b) => (
				method(a, b) ? base : (base * -1)
			);

			// We sort the items
			return items.sort(methodWrapper);
		},

		searchItems(items) {
			const search = this.cloneObject(this.criterias.search);

			// If we don't have a search term, there's no search to be made
			if (!search.term) {
				return items;
			}

			// Initialize Fuse with our search options
			const fuse = new Fuse(items, search.options);

			// Search our items through Fuse
			return fuse.search(search.term);
		},

		filterItems(items) {
			// If there's already a set of items prepared for our
			// current filter we use it as a starting point
			if (this.criterias.filters && this.criterias.filters.results) {
				return this.cloneObject(this.criterias.filters.results);
			}

			// Otherwise, we use the items we currently have
			return items;
		},

		processCriterias() {
			// Reset items
			let items = this.cloneObject(this.items);

			// First we filter
			items = this.filterItems(items);

			// Then we search..
			items = this.searchItems(items);

			// And finally we sort..
			items = this.sortItems(items);

			// Assign our new items
			this.itemsBuffer = items;

			this.$el.dispatchEvent(new Event('load'));
		},

		isColumnDisplayable(column) {
			return (!column.display || column.display[this.size] === true)
				&& (!Object.prototype.hasOwnProperty.call(column, 'width') || column.width !== 0);
		},

		// If there is no slot with this identifier, add table__row-block--raw
		// class and only display the "property" key of row
		isColumnRaw(column, item) {
			return (
				(!this.$scopedSlots[column.identifier])
				&& column.property
				&& item[column.property]
			);
		},

		onMouseover(rowId) {
			if (this.currentItem !== rowId) {
				this.currentItem = rowId;

				this.$emit('mouseover-row', this.currentItem);
			}
		},

		onMouseleave() {
			this.$emit('mouseleave-row', this.currentItem);

			this.currentItem = null;
		},
	},
};
</script>

<style lang="text/scss" src="./table.scss" />
